import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import DetectorHeader from "@/components/ripOffDetector/DetectorHeader";
import QuizProgress from "@/components/ripOffDetector/QuizProgress";
import QuestionCard from "@/components/ripOffDetector/QuestionCard";
import ContactForm from "@/components/ripOffDetector/ContactForm";
import SubmittedPanel from "@/components/ripOffDetector/SubmittedPanel";
import { Tag, BrushHighlight } from "@/components/sections/shared";
import FoldText from "@/components/FoldText/FoldText";
import { RIP_OFF_QUESTIONS } from "@/data/ripOffQuestions";
import { useDetectorForm } from "@/hooks/useDetectorForm";

const EASE = [0.16, 1, 0.3, 1];

function RipOffDetector() {
  const {
    step,
    totalSteps,
    isContactStep,
    progress,
    answers,
    contact,
    emailError,
    submitted,
    selectAnswer,
    goBack,
    updateContact,
    handleSubmit,
    reset,
  } = useDetectorForm();

  const currentQuestion = RIP_OFF_QUESTIONS[step];

  return (
    <section
      id="rip-off-detector"
      data-testid="rip-off-detector-page"
      className="relative min-h-screen overflow-hidden bg-[#0A0A0C]"
    >
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(138,43,226,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(138,43,226,0.4) 1px, transparent 1px)",
          backgroundSize: "76px 76px",
        }}
      />
      <div className="absolute -top-48 -right-48 w-[880px] h-[880px] rounded-full border border-[#9D4EDD]/[0.08]" />
      <div
        className="absolute bottom-0 left-0 w-[700px] h-[700px] pointer-events-none"
        style={{ background: "radial-gradient(circle at 15% 85%, rgba(138,43,226,0.14), transparent 60%)" }}
      />
      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] pointer-events-none"
        style={{ background: "radial-gradient(circle at 85% 10%, rgba(255,209,102,0.08), transparent 60%)" }}
      />

      <DetectorHeader />

      <div className="relative z-10 max-w-[640px] mx-auto px-6 py-16 sm:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-10"
        >
          <Tag testId="detector-page-tag">Free Tool</Tag>
          <h1 className="font-display font-semibold text-3xl sm:text-4xl !leading-[1.1] text-white">
            <FoldText
              text="Rip-Off Detector"
              splitBy="word"
              trigger="mount"
              fontSize="inherit"
              fontWeight="inherit"
              color="inherit"
            />
          </h1>
          <p className="mt-4 text-[#A1A1AA] font-light leading-relaxed">
            Answer 5 quick questions. We'll email you a plain-English Claim Check summary.
          </p>
        </motion.div>

        {!submitted && <QuizProgress step={step} total={totalSteps} progress={progress} isContactStep={isContactStep} />}

        <AnimatePresence mode="wait">
          {submitted ? (
            <SubmittedPanel email={contact.email} onReset={reset} />
          ) : isContactStep ? (
            <ContactForm contact={contact} emailError={emailError} onChange={updateContact} onSubmit={handleSubmit} />
          ) : (
            <QuestionCard
              question={currentQuestion}
              selectedOption={answers[currentQuestion.id]}
              onSelect={selectAnswer}
            />
          )}
        </AnimatePresence>

        {!submitted && step > 0 && (
          <button
            type="button"
            data-testid="detector-back-btn"
            onClick={goBack}
            className="group mt-6 inline-flex items-center gap-2 font-mono-file text-[11px] tracking-[0.2em] text-[#71717A] hover:text-[#FFD166] transition-colors duration-200"
          >
            <ArrowLeft size={13} className="transition-transform duration-300 group-hover:-translate-x-1" />
            BACK
          </button>
        )}

        <p className="mt-10 text-xs text-[#71717A] font-light">
          The Rip-Off Detector provides educational guidance and does not provide legal advice,
          public adjusting services, or guaranteed claim outcomes.
        </p>
      </div>
    </section>
  );
}

export default RipOffDetector;
